import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Heart, Users, Calendar, DollarSign, Search, 
  ChevronRight, Mail, Clock, CheckCircle, AlertCircle,
  Bell, Settings, LogOut, Eye, UserPlus, Activity, 
  MessageSquare, ChevronDown, Tag, TrendingUp, Menu, X,
  Ticket, UserCheck, ArrowUpRight, FileText, Lightbulb
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import AdminAnalytics from '../components/AdminAnalytics';
import { toast } from 'sonner';

// Use relative URLs for same-origin requests (works in both preview and production)
const API = '/api';

// Debug logging
console.log('[AdminDashboard] Using relative API paths (same-origin)');

const AdminDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [activities, setActivities] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [promoStats, setPromoStats] = useState([]);
  const [weddingInsights, setWeddingInsights] = useState([]);
  const [systemHealth, setSystemHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null); // Global error state
  const [endpointErrors, setEndpointErrors] = useState({}); // Per-endpoint errors
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showSettings, setShowSettings] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  // Get admin ID from prop OR localStorage (same as AdminWeddingInsight)
  const getAdminId = useCallback(() => {
    // First try from prop
    if (user?.id) {
      console.log('[AdminDashboard] Using admin ID from prop:', user.id);
      return user.id;
    }
    // Fallback: read from localStorage
    try {
      const savedUser = localStorage.getItem('vowflow_user');
      if (savedUser) {
        const parsed = JSON.parse(savedUser);
        if (parsed?.id) {
          console.log('[AdminDashboard] Using admin ID from localStorage:', parsed.id);
          return parsed.id;
        }
      }
    } catch (e) {
      console.error('[AdminDashboard] Failed to parse saved user:', e);
    }
    console.warn('[AdminDashboard] No admin ID available');
    return null;
  }, [user?.id]);

  // Helper to fetch with error tracking per endpoint
  const fetchWithDebug = async (endpoint, adminId, name) => {
    const url = `${API}${endpoint}`;
    const fullUrl = `${window.location.origin}${url}`;
    console.log(`[AdminDashboard] Fetching ${name} from: ${fullUrl}`);
    
    try {
      const response = await axios.get(url, { headers: { 'admin-id': adminId } });
      console.log(`[AdminDashboard] ${name} response:`, response.status);
      return { success: true, data: response.data, name, url: fullUrl };
    } catch (error) {
      const status = error.response?.status || 'NETWORK';
      const detail = error.response?.data?.detail || error.response?.data || error.message;
      console.error(`[AdminDashboard] ${name} FAILED:`, status, detail);
      return { 
        success: false, 
        name, 
        url: fullUrl, 
        status, 
        detail: typeof detail === 'object' ? JSON.stringify(detail) : String(detail)
      };
    }
  };

  const loadDashboardData = useCallback(async () => {
    const adminId = getAdminId();
    if (!adminId) {
      console.error('[AdminDashboard] No admin ID available for API calls');
      setLoadError({ status: 'AUTH', message: 'Admin ID not available. Please log in again.', url: 'N/A' });
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setLoadError(null);
    setEndpointErrors({});
    
    console.log('[AdminDashboard] Loading dashboard data with admin-id:', adminId);
    console.log('[AdminDashboard] API base:', window.location.origin + API);
    
    // Fetch each endpoint individually to track errors
    const results = await Promise.all([
      fetchWithDebug('/admin/dashboard-stats', adminId, 'Stats'),
      fetchWithDebug('/admin/clients?limit=10', adminId, 'Clients'),
      fetchWithDebug('/admin/upcoming-milestones?days=30', adminId, 'Milestones'),
      fetchWithDebug('/admin/activity-feed', adminId, 'Activity'),
      fetchWithDebug('/admin/client-analytics', adminId, 'Analytics'),
      fetchWithDebug('/admin/recent-users?limit=10', adminId, 'RecentUsers'),
      fetchWithDebug('/admin/promo-stats', adminId, 'PromoStats'),
      fetchWithDebug('/admin/wedding-insights', adminId, 'WeddingInsights'),
      fetchWithDebug('/admin/system-health', adminId, 'SystemHealth')
    ]);
    
    // Process results and collect errors
    const errors = {};
    
    results.forEach(result => {
      if (!result.success) {
        errors[result.name] = { url: result.url, status: result.status, detail: result.detail };
      } else {
        // Set state for successful fetches
        switch (result.name) {
          case 'Stats':
            setStats(result.data);
            break;
          case 'Clients':
            setClients(result.data.clients || []);
            break;
          case 'Milestones':
            setMilestones(result.data.milestones || []);
            break;
          case 'Activity':
            setActivities(result.data.activities || []);
            break;
          case 'Analytics':
            setAnalytics(result.data);
            break;
          case 'RecentUsers':
            setRecentUsers(result.data.users || []);
            break;
          case 'PromoStats':
            setPromoStats(result.data.promo_codes || []);
            break;
          case 'WeddingInsights':
            setWeddingInsights(result.data.purchases || []);
            break;
          case 'SystemHealth':
            setSystemHealth(result.data);
            break;
          default:
            break;
        }
      }
    });
    
    setEndpointErrors(errors);
    
    // If ALL endpoints failed, show global error
    if (Object.keys(errors).length === results.length) {
      const firstError = errors[Object.keys(errors)[0]];
      setLoadError({ 
        status: firstError.status, 
        message: firstError.detail, 
        url: firstError.url 
      });
    }
    
    setLoading(false);
  }, [getAdminId]);

  useEffect(() => {
    // Wait for admin ID to be available, then load data
    const adminId = getAdminId();
    if (adminId) {
      loadDashboardData();
    } else {
      // If no admin ID after a short delay, show error
      const timer = setTimeout(() => {
        if (!getAdminId()) {
          setLoadError({ status: 'AUTH', message: 'Not authenticated. Please log in as admin.', url: 'N/A' });
          setLoading(false);
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [getAdminId, loadDashboardData]);

  const handleSearch = async () => {
    const adminId = getAdminId();
    if (!adminId) return;
    
    try {
      const res = await axios.get(`${API}/admin/clients`, {
        headers: { 'admin-id': adminId },
        params: { search: searchQuery, status: filterStatus !== 'all' ? filterStatus : undefined }
      });
      setClients(res.data.clients);
    } catch (error) {
      toast.error('Search failed');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    const adminId = getAdminId();
    if (!adminId) return;
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 3) {
      toast.error('Password must be at least 3 characters long');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        {
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        },
        { headers: { 'user-id': adminId } }
      );
      
      toast.success('Password changed successfully!');
      setShowSettings(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'imminent': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'upcoming': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'planning': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'past': return 'bg-stone-500/20 text-stone-400 border-stone-500/30';
      default: return 'bg-stone-500/20 text-stone-400 border-stone-500/30';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'imminent': return 'This Month';
      case 'upcoming': return 'Upcoming';
      case 'planning': return 'Planning';
      case 'past': return 'Completed';
      default: return 'No Date';
    }
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getTierBadge = (tier) => {
    if (tier === 'premium') return 'bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30';
    if (tier === 'basic') return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    return 'bg-stone-500/20 text-stone-400 border-stone-500/30';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1C1917] flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <Heart className="w-12 h-12 text-[#881337] animate-pulse" />
          <p className="text-white/60">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  // Error state with debug panel
  if (loadError) {
    return (
      <div className="min-h-screen bg-[#1C1917] flex items-center justify-center p-4">
        <div className="text-center max-w-lg">
          <AlertCircle className="w-16 h-16 text-red-400/60 mx-auto mb-4" />
          <h2 className="text-xl text-white/60 mb-2">Failed to load dashboard</h2>
          <p className="text-white/40 mb-4">
            {loadError.status === 'AUTH' 
              ? 'Please log in as admin to view the dashboard.'
              : 'There was an error loading dashboard data.'}
          </p>
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-left overflow-x-auto mb-4">
            <p className="text-red-400 text-sm font-mono break-all">
              <strong>Status:</strong> {loadError.status}<br />
              <strong>URL:</strong> {loadError.url}<br />
              <strong>Detail:</strong> {loadError.message}
            </p>
          </div>
          <p className="text-white/30 text-xs mb-4">
            Origin: {window.location.origin}
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => loadDashboardData()}
              className="px-4 py-2 bg-[#881337] text-white rounded-lg hover:bg-[#9F1239] transition"
            >
              Retry
            </button>
            <button
              onClick={() => navigate('/admin/login')}
              className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition"
            >
              Re-login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1C1917]">
      {/* Top Navigation */}
      <nav className="bg-[#1C1917]/95 backdrop-blur-xl border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-[1800px] mx-auto px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center justify-between">
            {/* Logo & Mobile Menu */}
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="md:hidden p-2 text-white/60 hover:text-white"
                data-testid="mobile-menu-toggle"
              >
                {showMobileMenu ? <X size={24} /> : <Menu size={24} />}
              </button>
              <Heart className="text-[#881337]" size={28} fill="#881337" />
              <span className="text-xl font-serif font-bold text-white">VowFlow</span>
              <span className="hidden sm:inline text-xs bg-[#881337] text-white px-2 py-0.5 rounded-full ml-2">ADMIN</span>
            </div>
            
            {/* Desktop Nav Actions */}
            <div className="hidden md:flex items-center gap-3">
              <button 
                onClick={() => navigate('/admin/feedback')}
                className="px-4 py-2 bg-amber-600/20 hover:bg-amber-600/30 text-amber-400 border border-amber-600/30 rounded-full flex items-center gap-2 transition-all text-sm"
                data-testid="feedback-btn"
              >
                <MessageSquare size={16} />
                Feedback
              </button>
              <button 
                onClick={() => navigate('/admin/email-clients')}
                className="px-4 py-2 bg-[#881337] hover:bg-[#9F1239] text-white rounded-full flex items-center gap-2 transition-all text-sm"
                data-testid="email-clients-btn"
              >
                <Mail size={16} />
                Email Clients
              </button>
              <button 
                className="p-2 text-white/60 hover:text-white transition-colors relative"
                data-testid="notifications-btn"
              >
                <Bell size={20} />
                {milestones.length > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                )}
              </button>
              <button 
                onClick={() => setShowSettings(true)}
                className="p-2 text-white/60 hover:text-white transition-colors"
                data-testid="settings-btn"
              >
                <Settings size={20} />
              </button>
              <div className="h-6 w-px bg-white/10" />
              
              {/* User Menu Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-3 px-3 py-2 rounded-full hover:bg-white/5 transition-colors"
                  data-testid="user-menu-btn"
                >
                  <div className="w-8 h-8 rounded-full bg-[#881337] flex items-center justify-center text-white text-sm font-medium">
                    {user.partner1_name?.[0] || 'A'}
                  </div>
                  <div className="text-sm text-left hidden lg:block">
                    <p className="text-white font-medium">{user.partner1_name}</p>
                    <p className="text-white/40 text-xs">Administrator</p>
                  </div>
                  <ChevronDown size={16} className={`text-white/40 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>

                {showUserMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                    <div className="absolute right-0 top-full mt-2 w-56 bg-[#292524] border border-white/10 rounded-xl shadow-xl z-50 overflow-hidden">
                      <div className="px-4 py-3 border-b border-white/10">
                        <p className="text-white font-medium">{user.partner1_name}</p>
                        <p className="text-white/40 text-xs truncate">{user.email}</p>
                        <span className="inline-block mt-1 text-xs bg-[#881337]/20 text-[#881337] px-2 py-0.5 rounded-full">
                          {user.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                        </span>
                      </div>
                      <div className="py-1">
                        <button
                          onClick={() => { setShowUserMenu(false); setShowSettings(true); }}
                          className="w-full px-4 py-2.5 text-left text-white/80 hover:bg-white/5 flex items-center gap-3 transition-colors"
                        >
                          <Settings size={16} />
                          Settings
                        </button>
                        <button
                          onClick={() => { setShowUserMenu(false); navigate('/dashboard'); }}
                          className="w-full px-4 py-2.5 text-left text-white/80 hover:bg-white/5 flex items-center gap-3 transition-colors"
                        >
                          <Eye size={16} />
                          View as Client
                        </button>
                      </div>
                      <div className="border-t border-white/10 py-1">
                        <button
                          onClick={() => { setShowUserMenu(false); onLogout(); }}
                          className="w-full px-4 py-2.5 text-left text-red-400 hover:bg-red-500/10 flex items-center gap-3 transition-colors"
                          data-testid="logout-btn"
                        >
                          <LogOut size={16} />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Mobile User Avatar */}
            <div className="md:hidden">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="w-8 h-8 rounded-full bg-[#881337] flex items-center justify-center text-white text-sm font-medium"
              >
                {user.partner1_name?.[0] || 'A'}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {showMobileMenu && (
          <div className="md:hidden border-t border-white/10 bg-[#292524]">
            <div className="px-4 py-3 space-y-2">
              <button 
                onClick={() => { navigate('/admin/feedback'); setShowMobileMenu(false); }}
                className="w-full px-4 py-3 bg-amber-600/20 text-amber-400 rounded-xl flex items-center gap-3"
              >
                <MessageSquare size={18} />
                Feedback
              </button>
              <button 
                onClick={() => { navigate('/admin/email-clients'); setShowMobileMenu(false); }}
                className="w-full px-4 py-3 bg-[#881337] text-white rounded-xl flex items-center gap-3"
              >
                <Mail size={18} />
                Email Clients
              </button>
              <button 
                onClick={() => { setShowSettings(true); setShowMobileMenu(false); }}
                className="w-full px-4 py-3 bg-white/5 text-white rounded-xl flex items-center gap-3"
              >
                <Settings size={18} />
                Settings
              </button>
              <button 
                onClick={() => { onLogout(); setShowMobileMenu(false); }}
                className="w-full px-4 py-3 bg-red-500/10 text-red-400 rounded-xl flex items-center gap-3"
              >
                <LogOut size={18} />
                Sign Out
              </button>
            </div>
          </div>
        )}
      </nav>

      <div className="max-w-[1800px] mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Per-Widget Error Banner */}
        {Object.keys(endpointErrors).length > 0 && (
          <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-4" data-testid="endpoint-errors-panel">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-red-400 font-medium text-sm">Some widgets failed to load</h3>
                  <p className="text-white/50 text-xs mt-1">
                    {Object.keys(endpointErrors).length} of 8 data sources failed. Other data is still available.
                  </p>
                  <div className="mt-3 space-y-2 max-h-40 overflow-y-auto">
                    {Object.entries(endpointErrors).map(([name, err]) => (
                      <div key={name} className="bg-red-500/10 rounded-lg p-2 text-xs font-mono">
                        <p className="text-red-400">
                          <strong>{name}:</strong> {err.status}
                        </p>
                        <p className="text-white/40 break-all">{err.url}</p>
                        <p className="text-white/50 mt-1">{err.detail}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <button
                onClick={() => loadDashboardData()}
                className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-xs font-medium transition flex items-center gap-1.5 flex-shrink-0"
                data-testid="retry-failed-widgets"
              >
                <Activity size={12} />
                Retry All
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-4xl font-serif text-white mb-1 md:mb-2">Dashboard</h1>
          <p className="text-white/50 text-sm md:text-base">Manage your clients and monitor wedding progress</p>
        </div>

        {/* System Health Quick Widget */}
        {systemHealth && (
          <div 
            onClick={() => navigate('/admin/monitoring')}
            className={`mb-6 p-4 rounded-xl cursor-pointer transition-all hover:scale-[1.01] ${
              systemHealth.overall_status === 'healthy' ? 'bg-green-900/30 border border-green-700/50' :
              systemHealth.overall_status === 'attention_needed' ? 'bg-orange-900/30 border border-orange-700/50' :
              'bg-red-900/30 border border-red-700/50'
            }`}
            data-testid="system-health-widget"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-3 h-3 rounded-full ${
                  systemHealth.overall_status === 'healthy' ? 'bg-green-500' :
                  systemHealth.overall_status === 'attention_needed' ? 'bg-orange-500 animate-pulse' :
                  'bg-red-500 animate-pulse'
                }`} />
                <div>
                  <span className="text-white font-medium">System Health: </span>
                  <span className={`capitalize ${
                    systemHealth.overall_status === 'healthy' ? 'text-green-400' :
                    systemHealth.overall_status === 'attention_needed' ? 'text-orange-400' :
                    'text-red-400'
                  }`}>
                    {systemHealth.overall_status === 'attention_needed' ? 'Attention Needed' : systemHealth.overall_status}
                  </span>
                </div>
                {systemHealth.unacknowledged_critical > 0 && (
                  <span className="px-2 py-1 bg-red-500 text-white text-xs rounded-full animate-pulse">
                    {systemHealth.unacknowledged_critical} critical alert{systemHealth.unacknowledged_critical > 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${
                    systemHealth.components?.database?.status === 'healthy' ? 'bg-green-500' : 'bg-red-500'
                  }`} />
                  <span className="text-white/60">DB</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${
                    systemHealth.components?.stripe?.status === 'healthy' ? 'bg-green-500' : 
                    systemHealth.components?.stripe?.status === 'degraded' ? 'bg-yellow-500' : 'bg-red-500'
                  }`} />
                  <span className="text-white/60">Stripe</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${
                    systemHealth.components?.email?.status === 'healthy' ? 'bg-green-500' : 'bg-gray-500'
                  }`} />
                  <span className="text-white/60">Email</span>
                </div>
                <span className="text-white/40 text-xs flex items-center gap-1">
                  View Details <ArrowUpRight size={12} />
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Tab Navigation for Mobile */}
        <div className="flex md:hidden gap-2 mb-6 overflow-x-auto pb-2 -mx-4 px-4">
          {['overview', 'clients', 'promos', 'insights', 'activity'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all ${
                activeTab === tab 
                  ? 'bg-[#881337] text-white' 
                  : 'bg-white/5 text-white/60 hover:bg-white/10'
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Stats Grid */}
        <div className={`grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8 ${activeTab !== 'overview' && activeTab !== 'clients' ? 'hidden md:grid' : ''}`}>
          <button
            onClick={() => navigate('/admin/clients')}
            className={`rounded-xl md:rounded-2xl p-4 md:p-6 text-white text-left transition-all cursor-pointer group ${
              endpointErrors.Stats
                ? 'bg-gradient-to-br from-red-900/50 to-red-800/50'
                : 'bg-gradient-to-br from-[#881337] to-[#9F1239] hover:from-[#9F1239] hover:to-[#881337]'
            }`}
            data-testid="stat-total-clients"
          >
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <Users size={20} className="opacity-80" />
              <span className={`text-[10px] md:text-xs px-2 py-0.5 rounded-full ${
                endpointErrors.Stats ? 'bg-red-500/20 text-red-300' : 'bg-white/20'
              }`}>
                {endpointErrors.Stats ? 'Error' : 'Total'}
              </span>
            </div>
            {endpointErrors.Stats ? (
              <>
                <p className="text-lg md:text-2xl font-serif font-light text-red-300">—</p>
                <p className="text-red-300/60 text-xs mt-1">Failed to load</p>
              </>
            ) : (
              <>
                <p className="text-2xl md:text-4xl font-serif font-light">{stats?.total_clients || 0}</p>
                <p className="text-rose-200 text-xs md:text-sm mt-1 flex items-center gap-1">
                  Active Clients 
                  <ArrowUpRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </p>
              </>
            )}
          </button>

          <button
            onClick={() => navigate('/admin/email-automation')}
            className="bg-[#292524] border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-6 text-white hover:border-purple-400/30 transition-all text-left cursor-pointer group"
            data-testid="stat-email-campaigns"
          >
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <Mail size={20} className="text-purple-400" />
              <span className="text-[10px] md:text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full">Auto</span>
            </div>
            <p className="text-2xl md:text-4xl font-serif font-light">17</p>
            <p className="text-white/50 text-xs md:text-sm mt-1 flex items-center gap-1">
              Email Campaigns
              <ArrowUpRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
            </p>
          </button>

          <button
            onClick={() => navigate('/admin/revenue')}
            className={`bg-[#292524] border rounded-xl md:rounded-2xl p-4 md:p-6 text-white text-left cursor-pointer group transition-all ${
              endpointErrors.Stats 
                ? 'border-red-500/30 hover:border-red-500/50' 
                : 'border-white/10 hover:border-green-400/30'
            }`}
            data-testid="stat-revenue"
          >
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <DollarSign size={20} className={endpointErrors.Stats ? 'text-red-400' : 'text-green-400'} />
              <span className={`text-[10px] md:text-xs px-2 py-0.5 rounded-full ${
                endpointErrors.Stats 
                  ? 'bg-red-500/20 text-red-400' 
                  : 'bg-green-500/20 text-green-400'
              }`}>
                {endpointErrors.Stats ? 'Error' : 'Monthly'}
              </span>
            </div>
            {endpointErrors.Stats ? (
              <>
                <p className="text-lg md:text-2xl font-serif font-light text-red-400">—</p>
                <p className="text-red-400/60 text-xs mt-1">Failed to load</p>
              </>
            ) : (
              <>
                <p className="text-2xl md:text-4xl font-serif font-light">${stats?.monthly_revenue?.toFixed(0) || 0}</p>
                <p className="text-white/50 text-xs md:text-sm mt-1 flex items-center gap-1">
                  Revenue
                  <ArrowUpRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </p>
              </>
            )}
          </button>

          <button
            onClick={() => navigate('/admin/new-signups')}
            className={`bg-[#292524] border rounded-xl md:rounded-2xl p-4 md:p-6 text-white text-left cursor-pointer group transition-all ${
              endpointErrors.Stats 
                ? 'border-red-500/30 hover:border-red-500/50' 
                : 'border-white/10 hover:border-blue-400/30'
            }`}
            data-testid="stat-new-signups"
          >
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <UserPlus size={20} className={endpointErrors.Stats ? 'text-red-400' : 'text-blue-400'} />
              <span className={`text-[10px] md:text-xs px-2 py-0.5 rounded-full ${
                endpointErrors.Stats 
                  ? 'bg-red-500/20 text-red-400' 
                  : 'bg-blue-500/20 text-blue-400'
              }`}>
                {endpointErrors.Stats ? 'Error' : 'Week'}
              </span>
            </div>
            {endpointErrors.Stats ? (
              <>
                <p className="text-lg md:text-2xl font-serif font-light text-red-400">—</p>
                <p className="text-red-400/60 text-xs mt-1">Failed to load</p>
              </>
            ) : (
              <>
                <p className="text-2xl md:text-4xl font-serif font-light">{stats?.new_signups_this_week || 0}</p>
                <p className="text-white/50 text-xs md:text-sm mt-1 flex items-center gap-1">
                  New Signups
                  <ArrowUpRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </p>
              </>
            )}
          </button>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 md:gap-6">
          
          {/* Recently Active Users - New Section */}
          <div className={`xl:col-span-4 bg-[#292524] border border-white/10 rounded-xl md:rounded-2xl overflow-hidden ${activeTab !== 'overview' && activeTab !== 'activity' ? 'hidden md:block' : ''}`}>
            <div className="p-4 md:p-6 border-b border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UserCheck size={18} className="text-green-400" />
                  <h2 className="text-base md:text-lg font-serif text-white">Recently Active</h2>
                </div>
                <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full">
                  Live
                </span>
              </div>
            </div>
            
            <div className="p-4 md:p-6 space-y-3 max-h-[400px] overflow-y-auto">
              {recentUsers.length === 0 ? (
                <p className="text-white/40 text-sm text-center py-4">No recent activity</p>
              ) : (
                recentUsers.slice(0, 8).map((recentUser, idx) => (
                  <div 
                    key={idx}
                    onClick={() => navigate(`/admin/client/${recentUser.id}`)}
                    className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 cursor-pointer transition-all group"
                    data-testid={`recent-user-${idx}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-[#881337]/30 flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                      {recentUser.partner1_name?.[0] || '?'}{recentUser.partner2_name?.[0] || ''}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">
                        {recentUser.partner1_name} & {recentUser.partner2_name}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Clock size={10} className="text-white/40" />
                        <span className="text-white/40 text-xs">
                          {formatTimeAgo(recentUser.last_login || recentUser.created_at)}
                        </span>
                        {recentUser.promo_code_used && (
                          <span className="text-[10px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded">
                            {recentUser.promo_code_used}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-white/20 group-hover:text-white/60 transition-colors" />
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Promo Code Usage - New Section */}
          <div className={`xl:col-span-4 bg-[#292524] border border-white/10 rounded-xl md:rounded-2xl overflow-hidden ${activeTab !== 'overview' && activeTab !== 'promos' ? 'hidden md:block' : ''}`}>
            <div className="p-4 md:p-6 border-b border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Ticket size={18} className="text-amber-400" />
                  <h2 className="text-base md:text-lg font-serif text-white">Promo Codes</h2>
                </div>
                <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded-full">
                  {promoStats.reduce((acc, p) => acc + p.redemption_count, 0)} used
                </span>
              </div>
            </div>
            
            <div className="p-4 md:p-6 space-y-3 max-h-[400px] overflow-y-auto">
              {promoStats.length === 0 ? (
                <p className="text-white/40 text-sm text-center py-4">No promo codes configured</p>
              ) : (
                promoStats.map((promo, idx) => (
                  <div 
                    key={idx}
                    className="p-4 rounded-xl bg-white/5 border border-white/5 hover:border-white/10 transition-all"
                    data-testid={`promo-code-${idx}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <code className="text-sm font-mono text-[#D4AF37] bg-[#D4AF37]/10 px-2 py-0.5 rounded">
                          {promo.code}
                        </code>
                        {!promo.active && (
                          <span className="text-[10px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-lg font-semibold text-white">{promo.redemption_count}</span>
                        {promo.max_redemptions && (
                          <span className="text-xs text-white/40">/ {promo.max_redemptions}</span>
                        )}
                      </div>
                    </div>
                    
                    <p className="text-white/50 text-xs mb-2">{promo.description}</p>
                    
                    {/* Progress bar for redemption */}
                    {promo.max_redemptions && (
                      <div className="w-full bg-white/10 rounded-full h-1.5 mb-2">
                        <div 
                          className="bg-[#D4AF37] h-1.5 rounded-full transition-all"
                          style={{ width: `${Math.min((promo.redemption_count / promo.max_redemptions) * 100, 100)}%` }}
                        />
                      </div>
                    )}
                    
                    <div className="flex items-center gap-3 text-xs text-white/40">
                      {promo.discount_percent > 0 && (
                        <span>{promo.discount_percent}% off</span>
                      )}
                      {promo.trial_days > 0 && (
                        <span>{promo.trial_days} day trial</span>
                      )}
                      {promo.allowed_tier && (
                        <span className="capitalize">{promo.allowed_tier === 'basic' ? 'Essentials' : promo.allowed_tier} only</span>
                      )}
                    </div>

                    {/* Recent users who used this code */}
                    {promo.recent_users && promo.recent_users.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/5">
                        <p className="text-[10px] uppercase tracking-wider text-white/30 mb-2">Recent Redemptions</p>
                        <div className="flex -space-x-2">
                          {promo.recent_users.slice(0, 5).map((u, i) => (
                            <div 
                              key={i}
                              className="w-7 h-7 rounded-full bg-[#881337]/50 border-2 border-[#292524] flex items-center justify-center text-[10px] text-white"
                              title={`${u.partner1_name} & ${u.partner2_name}`}
                            >
                              {u.partner1_name?.[0]}{u.partner2_name?.[0]}
                            </div>
                          ))}
                          {promo.recent_users.length > 5 && (
                            <div className="w-7 h-7 rounded-full bg-white/10 border-2 border-[#292524] flex items-center justify-center text-[10px] text-white">
                              +{promo.recent_users.length - 5}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Needs Attention */}
          <div className={`xl:col-span-4 bg-[#292524] border border-white/10 rounded-xl md:rounded-2xl overflow-hidden ${activeTab !== 'overview' && activeTab !== 'activity' ? 'hidden md:block' : ''}`}>
            <div className="p-4 md:p-6 border-b border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertCircle size={18} className="text-red-400" />
                  <h2 className="text-base md:text-lg font-serif text-white">Needs Attention</h2>
                </div>
                <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded-full">
                  {milestones.filter(m => m.needs_attention).length}
                </span>
              </div>
            </div>
            
            <div className="p-4 md:p-6 space-y-3 max-h-[400px] overflow-y-auto">
              {milestones.length === 0 ? (
                <p className="text-white/40 text-sm text-center py-4">All clients on track!</p>
              ) : (
                milestones.slice(0, 6).map((milestone, idx) => (
                  <div 
                    key={idx}
                    onClick={() => navigate(`/admin/client/${milestone.user?.id}`)}
                    className={`p-3 rounded-xl border cursor-pointer transition-all ${
                      milestone.needs_attention 
                        ? 'border-red-500/30 bg-red-500/5 hover:bg-red-500/10' 
                        : 'border-white/10 bg-white/5 hover:bg-white/10'
                    }`}
                    data-testid={`milestone-${idx}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-white text-sm font-medium">
                          {milestone.user?.partner1_name} & {milestone.user?.partner2_name}
                        </p>
                        <p className="text-white/40 text-xs mt-0.5">
                          {milestone.days_until} days until wedding
                        </p>
                      </div>
                      <span className="text-xs text-amber-400">{milestone.progress}%</span>
                    </div>
                    {milestone.needs_attention && (
                      <div className="mt-2 flex items-center gap-1 text-red-400 text-xs">
                        <AlertCircle size={12} />
                        Low progress - needs check-in
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Wedding Insights Section */}
        <div className={`mt-6 bg-[#292524] border border-white/10 rounded-xl md:rounded-2xl overflow-hidden ${activeTab !== 'overview' && activeTab !== 'insights' ? 'hidden md:block' : ''}`}>
          <div className="p-4 md:p-6 border-b border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Lightbulb size={18} className="text-[#D4AF37]" />
                <h2 className="text-base md:text-lg font-serif text-white">Wedding Insights</h2>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/admin/stripe-events')}
                  className="text-xs text-green-400 hover:text-green-300 font-medium flex items-center gap-1"
                  data-testid="stripe-events-btn"
                >
                  Stripe Events <ArrowUpRight size={12} />
                </button>
                <span className="text-xs bg-[#D4AF37]/20 text-[#D4AF37] px-2 py-1 rounded-full">
                  {weddingInsights.length} purchases
                </span>
                <button
                  onClick={() => navigate('/admin/wedding-insight')}
                  className="text-xs text-[#881337] hover:text-[#9F1239] font-medium flex items-center gap-1"
                  data-testid="view-all-insights-btn"
                >
                  View All <ArrowUpRight size={12} />
                </button>
              </div>
            </div>
          </div>
          
          <div className="p-4 md:p-6">
            {weddingInsights.length === 0 ? (
              <p className="text-white/40 text-sm text-center py-8">No Wedding Insight purchases yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead className="bg-white/5">
                    <tr className="text-left text-white/50 text-xs uppercase tracking-wider">
                      <th className="px-4 py-3 font-medium">Customer</th>
                      <th className="px-4 py-3 font-medium">Purchase Date</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Wedding Date</th>
                      <th className="px-4 py-3 font-medium">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {weddingInsights.slice(0, 10).map((insight, idx) => (
                      <tr 
                        key={insight.id || idx} 
                        className="hover:bg-white/5 transition-colors"
                        data-testid={`insight-row-${idx}`}
                      >
                        <td className="px-4 py-4">
                          <div>
                            <p className="text-white text-sm font-medium">{insight.name || 'N/A'}</p>
                            <p className="text-white/40 text-xs">{insight.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-white/70 text-sm">
                          {insight.created_at 
                            ? new Date(insight.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : '—'
                          }
                        </td>
                        <td className="px-4 py-4">
                          <span className={`text-xs px-2 py-1 rounded-full border ${
                            insight.status === 'intake_submitted' 
                              ? 'bg-green-500/20 text-green-400 border-green-500/30'
                              : insight.status === 'paid'
                              ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                              : insight.status === 'review_sent'
                              ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                              : 'bg-stone-500/20 text-stone-400 border-stone-500/30'
                          }`}>
                            {insight.status === 'intake_submitted' ? 'Intake Received' 
                              : insight.status === 'paid' ? 'Awaiting Intake'
                              : insight.status === 'review_sent' ? 'Review Sent'
                              : insight.status?.replace(/_/g, ' ') || 'Pending'}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-white/70 text-sm">
                          {insight.intake_data?.wedding_date 
                            ? new Date(insight.intake_data.wedding_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : '—'
                          }
                        </td>
                        <td className="px-4 py-4">
                          {insight.intake_data ? (
                            <div className="text-xs space-y-1">
                              <p className="text-white/60">{insight.intake_data.venue || 'Venue TBD'}</p>
                              <p className="text-white/40">{insight.intake_data.guest_count || '?'} guests · {insight.intake_data.planning_stage || '?'}</p>
                            </div>
                          ) : (
                            <span className="text-white/30 text-xs">No intake yet</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Analytics Section */}
        <div className={`mt-6 ${activeTab !== 'overview' ? 'hidden md:block' : ''}`}>
          {analytics && <AdminAnalytics analytics={analytics} onNavigate={navigate} />}
        </div>

        {/* Client List */}
        <div className={`mt-6 bg-[#292524] border border-white/10 rounded-xl md:rounded-2xl overflow-hidden ${activeTab !== 'overview' && activeTab !== 'clients' ? 'hidden md:block' : ''}`}>
          <div className="p-4 md:p-6 border-b border-white/10">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
              <h2 className="text-base md:text-lg font-serif text-white">Clients</h2>
              <button 
                onClick={() => navigate('/admin/clients')}
                className="text-sm text-[#881337] hover:text-[#9F1239] flex items-center gap-1"
                data-testid="view-all-clients-btn"
              >
                View All <ChevronRight size={16} />
              </button>
            </div>
            
            {/* Search & Filter */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/30" size={18} />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Search clients..."
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 h-10"
                  data-testid="search-clients-input"
                />
              </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm h-10"
                data-testid="filter-status-select"
              >
                <option value="all">All Status</option>
                <option value="upcoming">Upcoming</option>
                <option value="planning">Planning</option>
                <option value="past">Completed</option>
              </select>
              <Button 
                onClick={handleSearch} 
                className="bg-[#881337] hover:bg-[#9F1239] h-10"
                data-testid="search-btn"
              >
                Search
              </Button>
            </div>
          </div>

          {/* Client Table - Responsive */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead className="bg-white/5">
                <tr className="text-left text-white/50 text-xs uppercase tracking-wider">
                  <th className="px-4 md:px-6 py-3 font-medium">Couple</th>
                  <th className="px-4 md:px-6 py-3 font-medium">Wedding</th>
                  <th className="px-4 md:px-6 py-3 font-medium">Progress</th>
                  <th className="px-4 md:px-6 py-3 font-medium">Status</th>
                  <th className="px-4 md:px-6 py-3 font-medium">Tier</th>
                  <th className="px-4 md:px-6 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {clients.map((client) => (
                  <tr 
                    key={client.id} 
                    className="hover:bg-white/5 transition-colors"
                    data-testid={`client-row-${client.id}`}
                  >
                    <td className="px-4 md:px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-[#881337]/30 flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                          {client.partner1_name?.[0]}{client.partner2_name?.[0]}
                        </div>
                        <div className="min-w-0">
                          <p className="text-white font-medium text-sm truncate">{client.partner1_name} & {client.partner2_name}</p>
                          <p className="text-white/40 text-xs truncate">{client.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 md:px-6 py-4 text-white/70 text-sm">
                      {client.wedding_date 
                        ? (() => {
                            const [year, month, day] = client.wedding_date.split('-').map(Number);
                            return new Date(year, month - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                          })()
                        : '—'
                      }
                      {client.days_until_wedding !== null && client.days_until_wedding >= 0 && (
                        <p className="text-white/40 text-xs">{client.days_until_wedding}d away</p>
                      )}
                    </td>
                    <td className="px-4 md:px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-16 md:w-24 bg-white/10 rounded-full h-1.5">
                          <div 
                            className="bg-[#881337] h-1.5 rounded-full transition-all"
                            style={{ width: `${client.progress}%` }}
                          />
                        </div>
                        <span className="text-white/70 text-xs">{client.progress}%</span>
                      </div>
                    </td>
                    <td className="px-4 md:px-6 py-4">
                      <span className={`text-xs px-2 py-1 rounded-full border ${getStatusColor(client.wedding_status)}`}>
                        {getStatusLabel(client.wedding_status)}
                      </span>
                    </td>
                    <td className="px-4 md:px-6 py-4">
                      <span className={`text-xs px-2 py-1 rounded-full border capitalize ${getTierBadge(client.subscription_tier)}`}>
                        {client.subscription_tier === 'basic' ? 'Essentials' : client.subscription_tier || 'Free'}
                      </span>
                    </td>
                    <td className="px-4 md:px-6 py-4">
                      <div className="flex items-center gap-1">
                        <button 
                          onClick={() => navigate(`/admin/client/${client.id}`)}
                          className="p-2 text-white/40 hover:text-white transition-colors rounded-lg hover:bg-white/5"
                          title="View Details"
                          data-testid={`view-client-${client.id}`}
                        >
                          <Eye size={16} />
                        </button>
                        <button 
                          onClick={() => navigate(`/admin/email-clients?client=${client.id}`)}
                          className="p-2 text-white/40 hover:text-[#881337] transition-colors rounded-lg hover:bg-white/5"
                          title="Send Email"
                          data-testid={`email-client-${client.id}`}
                        >
                          <Mail size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#292524] rounded-2xl border border-white/10 w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-serif text-white">Account Settings</h2>
              <button
                onClick={() => {
                  setShowSettings(false);
                  setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
                }}
                className="text-white/60 hover:text-white transition-colors p-1"
                data-testid="close-settings-btn"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handlePasswordChange} className="space-y-5">
              <div>
                <label className="text-white/60 text-sm mb-2 block">Current Password</label>
                <Input
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11"
                  placeholder="Enter current password"
                  required
                  data-testid="current-password-input"
                />
              </div>

              <div>
                <label className="text-white/60 text-sm mb-2 block">New Password</label>
                <Input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11"
                  placeholder="Enter new password"
                  required
                  data-testid="new-password-input"
                />
              </div>

              <div>
                <label className="text-white/60 text-sm mb-2 block">Confirm New Password</label>
                <Input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11"
                  placeholder="Confirm new password"
                  required
                  data-testid="confirm-password-input"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={() => {
                    setShowSettings(false);
                    setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
                  }}
                  className="flex-1 bg-white/5 hover:bg-white/10 text-white border border-white/10"
                  data-testid="cancel-settings-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={changingPassword}
                  className="flex-1 bg-[#881337] hover:bg-[#9F1239] text-white"
                  data-testid="save-password-btn"
                >
                  {changingPassword ? 'Changing...' : 'Change Password'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
